import { t as tabDefinition } from './definition52.js';
import { t as tabPanelDefinition } from './definition51.js';
import { V as VividElement, a as attr, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { i as keyArrowRight, h as keyArrowLeft, f as keyArrowDown, e as keyArrowUp, d as keyEnd, g as keyHome } from './key-codes.js';
import { u as uniqueId } from './strings.js';
import { l as limit } from './numbers.js';
import { c as classNames } from './class-names.js';
import { r as ref } from './ref.js';
import { s as slotted } from './slotted.js';
import { w as when } from './when.js';

const styles = ":host{display:block}.base{--_appearance-color-text: var(--_connotation-color-firm);--_appearance-color-fill: transparent;--_appearance-color-outline: transparent}.base.connotation-cta{--_connotation-color-firm: var(--vvd-tabs-cta-firm, var(--vvd-color-cta-600))}.base:not(.connotation-cta){--_connotation-color-firm: var(--vvd-tabs-accent-firm, var(--vvd-color-canvas-text))}.base{--_tabs-tablist-gutter: 8px;--_tabs-active-indicator-stroke-width: 2px;display:grid;box-sizing:border-box}.base.orientation-vertical{overflow:hidden;block-size:inherit;grid-template-columns:auto 1fr;grid-template-rows:1fr}.base:not(.orientation-vertical){grid-template-columns:1fr;grid-template-rows:auto 1fr;max-block-size:100%}.base.layout-stretch .scroll-shadow{flex:1}.base:not(.orientation-vertical) .scroll-shadow{position:relative;overflow:hidden;isolation:isolate}.base:not(.orientation-vertical) .scroll-shadow.start-scroll:before,.base:not(.orientation-vertical) .scroll-shadow.start-scroll:after,.base:not(.orientation-vertical) .scroll-shadow.end-scroll:before,.base:not(.orientation-vertical) .scroll-shadow.end-scroll:after{position:absolute;content:\"\";inline-size:10px;inset-block:0;opacity:0;transition:opacity .1s}.base:not(.orientation-vertical) .scroll-shadow.start-scroll:before{z-index:1;box-shadow:inset 7px 1px 5px -3px var(--vvd-color-neutral-950);inset-inline-start:0;opacity:.2}.base:not(.orientation-vertical) .scroll-shadow.end-scroll:after{box-shadow:inset -7px 1px 5px -3px var(--vvd-color-neutral-950);inset-inline-end:0;opacity:.2}.tabs{display:flex;overflow:hidden}.base.orientation-vertical .tabs{flex-direction:column;box-shadow:1px 0 0 0 var(--vvd-color-neutral-300)}.base.orientation-horizontal .tabs{flex-direction:row;border-bottom:1px solid var(--vvd-color-neutral-300)}.tablist{position:relative;display:grid;box-sizing:border-box;color:var(--_appearance-color-text)}.base.layout-stretch .tablist{--_tab-justify-content: center;--_tabs-tablist-column: 1fr}.tablist .base:not(.layout-stretch){--_tabs-tablist-column: auto}.base.orientation-vertical .tablist{padding:var(--_tabs-tablist-gutter);grid-row:1/2;grid-template-columns:auto 1fr;grid-template-rows:auto;inline-size:100%;place-self:flex-start end;row-gap:var(--_tabs-tablist-gutter)}.base:not(.orientation-vertical) .tablist{justify-content:flex-start;column-gap:var(--_tabs-tablist-gutter);grid-auto-flow:column;grid-template-rows:auto auto;inline-size:100%;padding-inline:var(--_tabs-tablist-gutter)}.base.has-action-items.orientation-vertical .tablist{padding-block-end:0}.base.has-action-items:not(.orientation-vertical) .tablist{padding-inline-end:0}.tablist-wrapper{--scrollbar-track-color: transparent;--scrollbar-thumb-color: color-mix(in srgb, var(--vvd-color-neutral-950), transparent 70%)}.tablist-wrapper{scrollbar-color:var(--scrollbar-thumb-color) var(--scrollbar-track-color);scrollbar-width:thin}.tablist-wrapper ::-webkit-scrollbar{width:4px}.tablist-wrapper ::-webkit-scrollbar-track{background:var(--scrollbar-track-color)}.tablist-wrapper ::-webkit-scrollbar-thumb{border:0;border-radius:4px;background-color:var(--scrollbar-thumb-color)}.base:not(.orientation-vertical) .tablist-wrapper{overflow:auto hidden;align-self:end;inline-size:100%}.base.orientation-vertical .tablist-wrapper{overflow:hidden auto;block-size:100%}.tablist-wrapper::-webkit-scrollbar{display:none}slot[name=action-items]{display:flex}.base.has-action-items.orientation-horizontal slot[name=action-items]{align-items:center;margin-inline:var(--_tabs-tablist-gutter)}.base.has-action-items.orientation-vertical slot[name=action-items]{margin-block:var(--_tabs-tablist-gutter)}.tabpanel{box-sizing:border-box;block-size:100%;min-block-size:0;min-inline-size:0}.base.scroll .tabpanel{overflow-y:auto;--scrollbar-track-color: transparent;--scrollbar-thumb-color: color-mix(in srgb, var(--vvd-color-neutral-950), transparent 70%)}.base.scroll .tabpanel{scrollbar-color:var(--scrollbar-thumb-color) var(--scrollbar-track-color);scrollbar-width:thin}.base.scroll .tabpanel ::-webkit-scrollbar{width:4px}.base.scroll .tabpanel ::-webkit-scrollbar-track{background:var(--scrollbar-track-color)}.base.scroll .tabpanel ::-webkit-scrollbar-thumb{border:0;border-radius:4px;background-color:var(--scrollbar-thumb-color)}.base.gutters-small .tabpanel{padding:calc(1px*(40 + 4*clamp(-1,var(--vvd-size-density, 0),2)) - (1px*(24 + 4*clamp(-1,var(--vvd-size-density, 0),2))))}.active-indicator{background:currentColor;margin-inline-start:calc(var(--_tabs-tablist-gutter) * -1)}.base.orientation-vertical .active-indicator{align-self:center;border-radius:2px;block-size:80%;grid-area:1/1/auto/auto;inline-size:var(--_tabs-active-indicator-stroke-width)}.base:not(.orientation-vertical) .active-indicator{position:absolute;z-index:1;border-radius:2px;block-size:var(--_tabs-active-indicator-stroke-width);grid-column:1/auto;inline-size:calc(var(--_tabs-active-tab-inline-size));inset-block-end:0;inset-inline-start:8px}.activeIndicatorTransition{transition:transform .2s ease-out 0s,inline-size .2s ease-out 0s}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const ACTIVE_TAB_WIDTH = "--_tabs-active-tab-inline-size";
const TABLIST_COLUMN = "--_tabs-tablist-column";
const TabsOrientation = {
  vertical: "vertical",
  horizontal: "horizontal"
};
class Tabs extends VividElement {
  constructor() {
    super(...arguments);
    // eslint-disable-next-line @nrwl/nx/workspace/no-attribute-default-value
    this.orientation = TabsOrientation.horizontal;
    this.#isLastTabSelectedAfterRemove = (oldValue, newValue, isTab = true) => {
      return oldValue.length > newValue.length && this.activetab.id === oldValue[oldValue.length - 1].getAttribute(
        isTab ? "id" : "aria-labelledby"
      );
    };
    // eslint-disable-next-line @nrwl/nx/workspace/no-attribute-default-value
    this.activeindicator = true;
    this.showActiveIndicator = true;
    this.prevActiveTabIndex = 0;
    this.activeTabIndex = 0;
    this.tabIds = [];
    this.tabpanelIds = [];
    this.change = () => {
      this.$emit("change", this.activetab);
    };
    this.isDisabledElement = (el) => {
      return el.getAttribute("aria-disabled") === "true";
    };
    this.isHiddenElement = (el) => {
      return el.hasAttribute("hidden");
    };
    this.isFocusableElement = (el) => {
      return !this.isDisabledElement(el) && !this.isHiddenElement(el);
    };
    this.setTabs = () => {
      const gridHorizontalProperty = "gridColumn";
      const gridVerticalProperty = "gridRow";
      const gridProperty = this.#isHorizontal() ? gridHorizontalProperty : gridVerticalProperty;
      this.activeTabIndex = this.getActiveIndex();
      this.showActiveIndicator = false;
      if (this.#isHorizontal()) {
        this.tablist.style.setProperty(
          "grid-template-columns",
          `repeat(${this.tabs.length}, var(${TABLIST_COLUMN}))`
        );
      } else {
        this.tablist.style.removeProperty("grid-template-columns");
      }
      this.tabs.forEach((tab, index) => {
        if (tab.slot === "tab") {
          const isActiveTab = this.activeTabIndex === index && this.isFocusableElement(tab);
          if (this.activeindicator && this.isFocusableElement(tab)) {
            this.showActiveIndicator = true;
          }
          const tabId = this.tabIds[index];
          const tabpanelId = this.tabpanelIds[index];
          tab.setAttribute("id", tabId);
          tab.setAttribute("aria-selected", isActiveTab ? "true" : "false");
          tab.setAttribute("aria-controls", tabpanelId);
          tab.addEventListener("click", this.handleTabClick);
          tab.addEventListener("keydown", this.#handleTabKeyDown);
          tab.setAttribute("tabindex", isActiveTab ? "0" : "-1");
          if (isActiveTab) {
            this.activetab = tab;
            this.activeid = tabId;
          }
        }
        tab.style[gridHorizontalProperty] = "";
        tab.style[gridVerticalProperty] = "";
        tab.style[gridProperty] = `${index + 1}`;
        !this.#isHorizontal() ? tab.classList.add("vertical") : tab.classList.remove("vertical");
      });
      this.#updateTabsConnotation();
    };
    this.setTabPanels = () => {
      this.tabpanels.forEach((tabpanel, index) => {
        const tabId = this.tabIds[index];
        const tabpanelId = this.tabpanelIds[index];
        tabpanel.setAttribute("id", tabpanelId);
        tabpanel.setAttribute("aria-labelledby", tabId);
        this.activeTabIndex !== index ? tabpanel.setAttribute("hidden", "") : tabpanel.removeAttribute("hidden");
      });
    };
    this.handleTabClick = (event) => {
      const selectedTab = event.currentTarget;
      if (selectedTab.nodeType === 1 && this.isFocusableElement(selectedTab)) {
        this.prevActiveTabIndex = this.activeTabIndex;
        this.activeTabIndex = this.tabs.indexOf(selectedTab);
        this.setComponent();
      }
    };
    this.#isHorizontal = () => {
      return this.orientation === TabsOrientation.horizontal;
    };
    this.#handleTabKeyDown = (event) => {
      if (this.#isHorizontal()) {
        switch (event.key) {
          case keyArrowLeft:
            event.preventDefault();
            this.adjustBackward(event);
            break;
          case keyArrowRight:
            event.preventDefault();
            this.adjustForward(event);
            break;
        }
      } else {
        switch (event.key) {
          case keyArrowUp:
            event.preventDefault();
            this.adjustBackward(event);
            break;
          case keyArrowDown:
            event.preventDefault();
            this.adjustForward(event);
            break;
        }
      }
      switch (event.key) {
        case keyHome:
          event.preventDefault();
          this.adjust(-this.activeTabIndex);
          break;
        case keyEnd:
          event.preventDefault();
          this.adjust(this.tabs.length - this.activeTabIndex - 1);
          break;
      }
    };
    this.adjustForward = (_) => {
      this.#moveToNextTab(1);
    };
    this.adjustBackward = (_) => {
      this.#moveToNextTab(-1);
    };
    this.#moveToTabByIndex = (group, index) => {
      const tab = group[index];
      this.activetab = tab;
      this.prevActiveTabIndex = this.activeTabIndex;
      this.activeTabIndex = index;
      tab.focus();
      this.setComponent();
    };
    this.scrollablePanel = false;
    this._actionItemsSlottedContent = [];
  }
  /**
   * @internal
   */
  orientationChanged() {
    if (this.$fastController.isConnected) {
      this.setTabs();
      this.setTabPanels();
      this.#handleActiveIndicatorPosition();
    }
    this.#patchIndicatorStyleTransition();
    if (!this.activeIndicatorRef) return;
    if (this.orientation === TabsOrientation.vertical) {
      this.activeIndicatorRef.style.removeProperty(ACTIVE_TAB_WIDTH);
    }
  }
  /**
   * @internal
   */
  activeidChanged(oldValue, _) {
    if (this.$fastController.isConnected && this.tabs.length <= this.tabpanels.length) {
      this.prevActiveTabIndex = this.tabs.findIndex(
        (item) => item.id === oldValue
      );
      this.setTabs();
      this.setTabPanels();
      this.#handleActiveIndicatorPosition();
    }
    this.#patchIndicatorStyleTransition();
    this.#scrollToIndex(this.activeTabIndex);
  }
  #isLastTabSelectedAfterRemove;
  /**
   * @internal
   */
  tabsChanged(oldValue, newValue) {
    if (this.$fastController.isConnected && this.tabs.length <= this.tabpanels.length) {
      this.tabIds = this.getTabIds();
      this.tabpanelIds = this.getTabPanelIds();
      this.setTabs();
      this.setTabPanels();
      this.#handleActiveIndicatorPosition(
        !this.#isLastTabSelectedAfterRemove(oldValue, newValue)
      );
    }
    this.#patchIndicatorStyleTransition();
    this.#updateScrollStatus();
  }
  /**
   * @internal
   */
  tabpanelsChanged(oldValue, newValue) {
    if (this.$fastController.isConnected && this.tabpanels.length <= this.tabs.length) {
      this.tabIds = this.getTabIds();
      this.tabpanelIds = this.getTabPanelIds();
      this.setTabs();
      this.setTabPanels();
      this.#handleActiveIndicatorPosition(
        !this.#isLastTabSelectedAfterRemove(oldValue, newValue, false)
      );
    }
    this.#patchIndicatorStyleTransition();
  }
  getActiveIndex() {
    const id = this.activeid;
    if (id !== void 0) {
      return this.tabIds.indexOf(this.activeid) === -1 ? 0 : this.tabIds.indexOf(this.activeid);
    } else {
      return 0;
    }
  }
  getTabIds() {
    return this.tabs.map((tab) => {
      return tab.getAttribute("id") ?? `tab-${uniqueId()}`;
    });
  }
  getTabPanelIds() {
    return this.tabpanels.map((tabPanel) => {
      return tabPanel.getAttribute("id") ?? `panel-${uniqueId()}`;
    });
  }
  setComponent() {
    if (this.activeTabIndex !== this.prevActiveTabIndex) {
      this.activeid = this.tabIds[this.activeTabIndex];
      this.focusTab();
      this.change();
    }
  }
  #isHorizontal;
  #handleTabKeyDown;
  /**
   * The adjust method for FASTTabs
   * @public
   * @remarks
   * This method allows the active index to be adjusted by numerical increments
   */
  adjust(adjustment) {
    const focusableTabs = this.tabs.filter((t) => this.isFocusableElement(t));
    const currentActiveTabIndex = focusableTabs.indexOf(this.activetab);
    const nextTabIndex = limit(
      0,
      focusableTabs.length - 1,
      currentActiveTabIndex + adjustment
    );
    const nextIndex = this.tabs.indexOf(focusableTabs[nextTabIndex]);
    if (nextIndex > -1) {
      this.#moveToTabByIndex(this.tabs, nextIndex);
    }
  }
  #moveToNextTab(direction) {
    const activeIndex = this.tabs.indexOf(this.activetab);
    for (let offset = 1; offset < this.tabs.length; offset++) {
      const index = (activeIndex + direction * offset + this.tabs.length) % this.tabs.length;
      if (this.isFocusableElement(this.tabs[index])) {
        this.#moveToTabByIndex(this.tabs, index);
        break;
      }
    }
  }
  #moveToTabByIndex;
  focusTab() {
    this.tabs[this.activeTabIndex].focus();
  }
  /**
   * @internal
   */
  connotationChanged() {
    this.#updateTabsConnotation();
  }
  #updateScrollStatus() {
    this.tablist.parentElement.dispatchEvent(new Event("scroll"));
  }
  get #shouldMoveIndicator() {
    return !(!this.activetab || !this.activeIndicatorRef || this.orientation === TabsOrientation.vertical || !this.showActiveIndicator);
  }
  #patchIndicatorStyleTransition() {
    if (!this.#shouldMoveIndicator) {
      return;
    }
    const width = this.activetab.getBoundingClientRect().width;
    this.activeIndicatorRef.style.setProperty(ACTIVE_TAB_WIDTH, `${width}px`);
  }
  connectedCallback() {
    super.connectedCallback();
    this.tabIds = this.getTabIds();
    this.tabpanelIds = this.getTabPanelIds();
    this.activeTabIndex = this.getActiveIndex();
    requestAnimationFrame(() => this.#updateScrollStatus());
    const scrollWrapper = this.tablist.parentElement;
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateScrollStatus();
      this.#patchIndicatorStyleTransition();
    });
    this.#resizeObserver.observe(scrollWrapper);
  }
  #resizeObserver;
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }
  #updateTabsConnotation() {
    if (this.tabs) {
      this.tabs.forEach((tab) => {
        if (tab.getAttribute("aria-selected") === "true") {
          tab.setAttribute("connotation", this.connotation);
        } else {
          tab.removeAttribute("connotation");
        }
      });
    }
  }
  get #tabListWrapper() {
    return this.shadowRoot.querySelector(".tablist-wrapper");
  }
  #scrollToIndex(index) {
    const tab = this.tabs?.[index];
    if (!tab) return;
    let left = 0;
    let top = 0;
    if (this.orientation === TabsOrientation.vertical) {
      if (index === this.tabs.length - 1) {
        top = this.#tabListWrapper.scrollHeight;
      }
      if (index > 0 && index < this.tabs.length - 1) {
        top = tab.offsetTop - this.#tabListWrapper.offsetHeight / 2 + tab.offsetHeight / 2;
      }
    } else {
      if (index === this.tabs.length - 1) {
        left = this.#tabListWrapper.scrollWidth;
      }
      if (index > 0 && index < this.tabs.length - 1) {
        left = tab.offsetLeft - this.#tabListWrapper.offsetWidth / 2 + tab.offsetWidth / 2;
      }
    }
    this.#tabListWrapper.scrollTo({ top, left, behavior: "smooth" });
  }
  #getGridProperty() {
    return this.#isHorizontal() ? "gridColumn" : "gridRow";
  }
  #getTranslateProperty() {
    return this.#isHorizontal() ? "translateX" : "translateY";
  }
  #handleActiveIndicatorPosition(animate = true) {
    if (this.showActiveIndicator && this.activeindicator) {
      this.#animateActiveIndicator(animate);
    }
  }
  #animateActiveIndicator(animate) {
    const offsetProperty = this.#isHorizontal() ? "offsetLeft" : "offsetTop";
    const currentOffset = this.activeIndicatorRef[offsetProperty];
    const currentGridValue = this.activeIndicatorRef.style[this.#getGridProperty()];
    this.activeIndicatorRef.style[this.#getGridProperty()] = `${this.activeTabIndex + 1}`;
    const targetOffset = this.activeIndicatorRef[offsetProperty];
    this.activeIndicatorRef.style[this.#getGridProperty()] = currentGridValue;
    const relativeOffset = targetOffset - currentOffset;
    this.activeIndicatorRef.style.transform = `${this.#getTranslateProperty()}(${relativeOffset}px)`;
    if (animate) {
      this.activeIndicatorRef.classList.add("activeIndicatorTransition");
    } else {
      this.activeIndicatorRef.classList.remove("activeIndicatorTransition");
    }
  }
  /**
   * @internal
   */
  activeIndicatorRefChanged() {
    this.activeIndicatorRef.addEventListener("transitionend", () => {
      this.activeIndicatorRef.style[this.#getGridProperty()] = `${this.activeTabIndex + 1}`;
      this.activeIndicatorRef.style.transform = `${this.#getTranslateProperty()}(0px)`;
      this.activeIndicatorRef.classList.remove("activeIndicatorTransition");
    });
  }
}
__decorateClass([
  attr
], Tabs.prototype, "orientation");
__decorateClass([
  attr
], Tabs.prototype, "activeid");
__decorateClass([
  observable
], Tabs.prototype, "tabs");
__decorateClass([
  observable
], Tabs.prototype, "tabpanels");
__decorateClass([
  attr({ mode: "boolean" })
], Tabs.prototype, "activeindicator");
__decorateClass([
  observable
], Tabs.prototype, "activeIndicatorRef");
__decorateClass([
  observable
], Tabs.prototype, "showActiveIndicator");
__decorateClass([
  observable
], Tabs.prototype, "tablist");
__decorateClass([
  attr
], Tabs.prototype, "connotation");
__decorateClass([
  attr
], Tabs.prototype, "gutters");
__decorateClass([
  attr({ mode: "boolean", attribute: "scrollable-panel" })
], Tabs.prototype, "scrollablePanel");
__decorateClass([
  attr({ attribute: "tabs-layout" })
], Tabs.prototype, "tabsLayout");
__decorateClass([
  observable
], Tabs.prototype, "_actionItemsSlottedContent");

const getClasses = ({
  connotation,
  orientation,
  gutters,
  scrollablePanel,
  tabsLayout,
  _actionItemsSlottedContent
}) => classNames(
  "base",
  `layout-${tabsLayout ?? "align-start"}`,
  [`connotation-${connotation}`, Boolean(connotation)],
  [`orientation-${orientation}`, Boolean(orientation)],
  `gutters-${gutters ?? "small"}`,
  ["scroll", Boolean(scrollablePanel)],
  ["has-action-items", Boolean(_actionItemsSlottedContent.length)]
);
function setNoScrollState(scrollShadow, scrollWrapper) {
  if (scrollWrapper.scrollWidth <= scrollWrapper.clientWidth) {
    scrollShadow.classList.toggle("start-scroll", false);
    scrollShadow.classList.toggle("end-scroll", false);
    return true;
  }
  return false;
}
function addStartShadow(scrollShadow, scrollWrapper) {
  scrollShadow.classList.toggle("start-scroll", scrollWrapper.scrollLeft > 0);
}
function addEndShadow(scrollShadow, scrollWrapper) {
  scrollShadow.classList.toggle(
    "end-scroll",
    scrollWrapper.scrollLeft < scrollWrapper.scrollWidth - scrollWrapper.clientWidth
  );
}
function setShadowWhenScrollTabs(_, { event }) {
  const scrollWrapper = event.currentTarget;
  const scrollShadow = scrollWrapper.parentElement;
  if (setNoScrollState(scrollShadow, scrollWrapper)) {
    return;
  }
  addStartShadow(scrollShadow, scrollWrapper);
  addEndShadow(scrollShadow, scrollWrapper);
}
const TabsTemplate = html`
	<template>
		<div class="${getClasses}">
			<div class="tabs">
				<div class="scroll-shadow">
					<div class="tablist-wrapper" @scroll="${setShadowWhenScrollTabs}">
						<div class="tablist" role="tablist" ${ref("tablist")}>
							<slot name="tab" ${slotted("tabs")}></slot>
							${when(
  (x) => x.showActiveIndicator,
  html`
									<div
										${ref("activeIndicatorRef")}
										class="active-indicator"
									></div>
								`
)}
						</div>
					</div>
				</div>
				<slot
					name="action-items"
					${slotted("_actionItemsSlottedContent")}
				></slot>
			</div>
			<div class="tabpanel" part="tab-panel">
				<slot name="tabpanel" ${slotted("tabpanels")}></slot>
			</div>
		</div>
	</template>
`;

const tabsDefinition = defineVividComponent(
  "tabs",
  Tabs,
  TabsTemplate,
  [tabDefinition, tabPanelDefinition],
  {
    styles
  }
);
const registerTabs = createRegisterFunction(tabsDefinition);

export { Tabs as T, registerTabs as r, tabsDefinition as t };
